import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchGolfCourses, getGolfCourseDetails, postCourse } from '../api';
import { PageHeader, Spinner, ErrorMsg } from '../components/UI';

export default function AddCoursePage() {
  const navigate = useNavigate();

  const [courseName, setCourseName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');

  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [adding, setAdding] = useState(null); // course id being added
  const [addError, setAddError] = useState('');

  const [showRequestModal, setShowRequestModal] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!courseName.trim()) return;
    setSearchError('');
    setResults(null);
    setSearching(true);
    try {
      const data = await searchGolfCourses(courseName, city, state, country);
      setResults(data);
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleAddCourse = async (apiCourse) => {
    setAddError('');
    setAdding(apiCourse.id);
    try {
      // Fetch tees
      const tees = await getGolfCourseDetails(apiCourse.id);
      await postCourse({
        api_course_id: String(apiCourse.id),
        name: apiCourse.club_name,
        address: apiCourse.location?.address,
        city: apiCourse.location?.city,
        state: apiCourse.location?.state,
        country: apiCourse.location?.country,
        tees,
      });
      navigate('/');
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(null);
    }
  };

  // ── Request Course Modal ──────────────────────────────────────────────────────
  function RequestCourseModal({ initialName = '', onClose }) {
    const [name,    setName]    = useState(initialName);
    const [city,    setCity]    = useState('');
    const [state,   setState]   = useState('');
    const [country, setCountry] = useState('');
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');
    const [success, setSuccess] = useState(false);
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      if (!name.trim())    { setError('Course name is required.'); return; }
      if (!country.trim()) { setError('Country is required.'); return; }
  
      const description = [
        '[COURSE REQUEST]',
        `Course: ${name.trim()}`,
        city.trim()    ? `City: ${city.trim()}`       : null,
        state.trim()   ? `State: ${state.trim()}`     : null,
        `Country: ${country.trim()}`,
      ].filter(Boolean).join(' | ');
  
      if (description.length > 300) {
        setError('Request is too long. Please shorten the course name or location details.');
        return;
      }
  
      setLoading(true);
      try {
        await reportIssue(description);
        setSuccess(true);
      } catch (err) {
        setError(err.message || 'Failed to submit request. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
          <div className="card w-full max-w-sm p-6 fade-in">
            {success ? (
              <>
                <div className="text-center py-4">
                  <div className="text-4xl mb-3">📬</div>
                  <h3 className="font-serif font-bold text-xl text-green-900 mb-2">Request Sent!</h3>
                  <p className="text-sm text-gray-500">
                    Thanks! Your course request has been submitted. We'll review it and add it as soon as possible.
                  </p>
                </div>
                <button onClick={onClose} className="btn-primary w-full mt-5">Done</button>
              </>
            ) : (
              <>
                <h3 className="font-serif font-bold text-xl text-green-900 mb-1">Request a Course</h3>
                <p className="text-sm text-gray-500 mb-5">
                  Can't find your course? Fill in what you know and we'll add it.
                </p>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                      Course Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      className="input-field"
                      placeholder="e.g. Pebble Beach Golf Links"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      maxLength={100}
                      autoFocus
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">City</label>
                    <input
                      className="input-field"
                      placeholder="e.g. Pebble Beach"
                      value={city}
                      onChange={e => setCity(e.target.value)}
                      maxLength={60}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">State / Province</label>
                    <input
                      className="input-field"
                      placeholder="e.g. California"
                      value={state}
                      onChange={e => setState(e.target.value)}
                      maxLength={60}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                      Country <span className="text-red-400">*</span>
                    </label>
                    <input
                      className="input-field"
                      placeholder="e.g. United States"
                      value={country}
                      onChange={e => setCountry(e.target.value)}
                      maxLength={60}
                      required
                    />
                  </div>
                  <ErrorMsg message={error} />
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={onClose} className="btn-secondary flex-1" disabled={loading}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      );
    }

  const formatAddress = (loc) => {
    return [loc?.address, loc?.city, loc?.state, loc?.country].filter(Boolean).join(', ');
  };

  return (
    <div className="page-container">
      <PageHeader back title="Add a Course" subtitle="Search for a course from the Golf API" />

      <div className="card p-5 mb-5">
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
              Course Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="e.g. Pebble Beach Golf Links"
              value={courseName}
              onChange={e => setCourseName(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">City</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Pebble Beach"
                value={city}
                onChange={e => setCity(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">State</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. KY"
                value={state}
                onChange={e => setState(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Country</label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. United States"
                value={country}
                onChange={e => setCountry(e.target.value)}
              />
            </div>
          </div>

          <ErrorMsg message={searchError} />

          <button
            type="submit"
            className="btn-primary w-full flex items-center justify-center gap-2"
            disabled={searching}
          >
            {searching ? <><Spinner size="sm" /> Searching...</> : '🔍 Search Courses'}
          </button>
        </form>
      </div>

      {/* Results */}
      {results !== null && (
        <div className="fade-in">
          <h2 className="font-serif font-semibold text-green-900 text-lg mb-3">
            {results.length} result{results.length !== 1 ? 's' : ''} found
          </h2>

          <ErrorMsg message={addError} />

          {results.length === 0 ? (
            <div className="card p-8 text-center">
              <div className="text-3xl mb-2">🔎</div>
              <p className="font-medium text-green-900 mb-1">No courses found</p>
              <p className="text-sm text-gray-500">Try adjusting your search terms or removing location filters.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map(course => (
                <div key={course.id} className="card p-4 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-green-900 truncate">{course.club_name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{formatAddress(course.location)}</p>
                  </div>
                  <button
                    onClick={() => handleAddCourse(course)}
                    className="btn-gold flex-shrink-0 flex items-center gap-2"
                    disabled={adding === course.id}
                  >
                    {adding === course.id ? <Spinner size="sm" /> : '+ Add Course'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <button
          className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl border-2 border-dashed border-amber-700/40 bg-amber-50 hover:bg-amber-100 hover:border-amber-700/60 transition-colors group"
          onClick={() => setShowRequestModal(true)}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
              <span className="text-amber-800 text-base font-bold leading-none">+</span>
            </div>
            <div className="text-left">
              <div className="text-sm font-medium text-amber-900">Can't find your course?</div>
              <div className="text-xs text-amber-700 mt-0.5">Submit a request and we'll add it</div>
            </div>
          </div>
          <span className="text-amber-700 text-sm group-hover:translate-x-0.5 transition-transform">→</span>
        </button>
      </div>

      {showRequestModal && (
        <RequestCourseModal
          initialName={courseName}
          onClose={() => setShowRequestModal(false)}
        />
      )}

    </div>
  );
}
